import {
  forwardRef,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
} from 'react';
import { Check } from 'lucide-react';
import { cn } from '@/utils/cn';

type CheckboxProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'type' | 'children'
> & {
  /** Label-Text neben der Checkbox */
  label: ReactNode;
  hasError?: boolean;
};

/**
 * Sizilianer-Checkbox mit Custom-Look.
 * Gold-Hintergrund + weißes Check-Icon im Checked-State.
 *
 * @example
 *   <Checkbox
 *     label={<>Ich akzeptiere die <a href="/agb">AGB</a></>}
 *     required
 *   />
 */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  function Checkbox({ label, hasError, className, id, ...props }, ref) {
    const generatedId = useId();
    const inputId = id ?? generatedId;

    return (
      <label
        htmlFor={inputId}
        className={cn(
          'inline-flex items-start gap-2.5 cursor-pointer',
          'group',
          props.disabled && 'opacity-50 cursor-not-allowed',
          className,
        )}
      >
        <span className="relative shrink-0 mt-0.5">
          <input
            ref={ref}
            id={inputId}
            type="checkbox"
            className="peer sr-only"
            {...props}
          />
          <span
            className={cn(
              'block size-5 rounded-sm bg-paper-2',
              'border transition-[color,background-color,border-color,transform,box-shadow]',
              hasError ? 'border-it-red' : 'border-rule',
              'peer-checked:border-gold peer-checked:bg-gold',
              'peer-focus-visible:outline-2 peer-focus-visible:outline-gold peer-focus-visible:outline-offset-2',
              'group-hover:border-gold',
            )}
          >
            <Check
              className={cn(
                'size-4 text-ink absolute left-0.5 top-0.5',
                'opacity-0 peer-checked:opacity-100 transition-opacity',
                'pointer-events-none',
              )}
              strokeWidth={3}
              aria-hidden="true"
            />
          </span>
        </span>
        <span className="font-sans text-sm text-ink-2 leading-relaxed select-none">
          {label}
        </span>
      </label>
    );
  },
);
